import { type ReactElement, useCallback } from 'react'
import { type LockableTeamField, type TeamData } from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { TextInput, Switch, Textarea } from '@mantine/core'
import { useProfile, useCanUpdateSunrunTeam, useLockedTeamFields, usePreference, useTranslation } from '@front/model'
import { LockedFieldTooltip, TButton } from '@components'
import { type SelectedPlace } from './types'
import { OfficeAddressInput } from './OfficeAddressInput'

type Props = {
  form: UseFormReturnType<TeamData>
  selectedPlace: SelectedPlace | undefined
  setSelectedPlace: (selectedPlace: SelectedPlace | undefined) => void
}

export const EditTeamForm = ({ form, selectedPlace, setSelectedPlace }: Props): ReactElement => {
  const { company } = useProfile()
  const { t } = useTranslation()

  const canUpdateName = useCanUpdateSunrunTeam(company?.companyId, form.values)
  const { lockedFields } = useLockedTeamFields(form.values.teamId)
  const isLocked = (field: LockableTeamField): boolean => lockedFields.includes(field)

  const { data: restrictionsEnabled } = usePreference('enableVisibilityRestrictions')

  const { getInputProps, setFieldValue } = form

  const handleAddressChange = useCallback((address: string) => setFieldValue('officeAddress', address), [setFieldValue])

  return (
    <>
      <LockedFieldTooltip locked={isLocked('name')}>
        <TextInput {...getInputProps('name')} disabled={!canUpdateName} label={t('Name')} />
      </LockedFieldTooltip>
      <LockedFieldTooltip locked={isLocked('description')}>
        <Textarea {...getInputProps('description')} label={t('Description')} placeholder={t('Description')} />
      </LockedFieldTooltip>
      <LockedFieldTooltip locked={isLocked('externalId')}>
        <TextInput {...getInputProps('externalId')} label={t('External ID')} />
      </LockedFieldTooltip>
      <LockedFieldTooltip locked={isLocked('officeAddress')}>
        <OfficeAddressInput
          {...getInputProps('officeAddress')}
          onAddressChange={handleAddressChange}
          onPlaceSelected={setSelectedPlace}
        />
      </LockedFieldTooltip>
      {selectedPlace?.url && (
        <TButton component='a' href={selectedPlace.url} icon='share_location' target='_blank' variant='ghost'>
          {t('View on Google Maps')}
        </TButton>
      )}
      <Switch
        {...getInputProps('isCloserBoundary', {
          type: 'checkbox',
        })}
        description={t('Closer Events for Setters in this team should be restricted to Closers only in this team')}
        label={t('Use Closers only in this team')}
      />
      {restrictionsEnabled && (
        <Switch
          {...getInputProps('restrictVisibility', {
            type: 'checkbox',
          })}
          description={t(
            'Allow configuring certain permissions to be confined within this team. This will also cause the team to act as a closer boundary.'
          )}
          label={t('Restrict Team')}
        />
      )}
    </>
  )
}
